/**
 * GA4 conversion helper.
 *
 * The gtag snippet in index.html only installs itself on the production
 * hosts. This module repeats that gate so events stay quiet during SSR,
 * prerender, local dev, Vercel previews, and automated browsers even if
 * something else defines window.gtag.
 */

const PRODUCTION_HOSTS = new Set(['www.milkywayplanner.com', 'milkywayplanner.com']);

const BLOCKED_PARAM_KEYS = new Set([
  'email',
  'name',
  'latitude',
  'longitude',
  'lat',
  'lng',
  'lon',
]);

const AFFILIATE_HOSTS = ['amazon.com', 'amzn.to', 'getrewardful.com'];

const DEDUPE_MS = 1000;
const ONCE_STORAGE_PREFIX = 'mw_ga_once:';
const PENDING_DONATION_PREFIX = 'mw_pending_donation:';
const PENDING_DONATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const CHECKOUT_SESSION_ID = /cs_(?:live|test)_[A-Za-z0-9]+/;
const DONATION_NONCE = /^[0-9a-f]{64}$/;

/** Query keys that must never reach GA4. Campaign and ad click ids stay. */
const PAGE_LOCATION_QUERY_KEYS_TO_DROP = new Set([
  'session_id',
  'access_token',
  'refresh_token',
  'code',
  'token',
  'email',
]);

const recent = new Map<string, number>();
const durableKeys = new Set<string>();
const memoryOnce = new Set<string>();

export const CHECKOUT_VALUE = {
  Premium: 25,
  'Workshop Leader': 200,
} as const;

export type CheckoutPlan = keyof typeof CHECKOUT_VALUE;
export type LocationType = 'custom' | 'catalog' | 'saved';

type TrackOptions = { onceKey?: string; dedupeKey?: string; beacon?: boolean };

type AnalyticsEventMap = {
  sign_up: { method: string };
  login: undefined;
  purchase: {
    transaction_id: string;
    value: number;
    currency: string;
    items: Array<{ item_name: CheckoutPlan }>;
  };
  donation: { value?: number; currency?: string };
  newsletter_signup: undefined;
  affiliate_click: { destination: string };
  calendar_pdf_download: { year: number };
  planner_run: { location_type: LocationType };
  begin_checkout: { plan: CheckoutPlan };
};

export type AnalyticsEventName = keyof AnalyticsEventMap;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export function normalizeAnalyticsHost(hostname: string): string {
  return hostname.replace(/\.$/, '').toLowerCase();
}

export function isProductionAnalyticsHost(hostname: string): boolean {
  return PRODUCTION_HOSTS.has(normalizeAnalyticsHost(hostname));
}

export function resetAnalyticsForTests() {
  recent.clear();
  durableKeys.clear();
  memoryOnce.clear();
}

function localStore(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function scrub(params: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!params) return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (BLOCKED_PARAM_KEYS.has(key.toLowerCase())) continue;
    if (Array.isArray(value)) {
      out[key] = value.map((item) =>
        item && typeof item === 'object'
          ? scrub(item as Record<string, unknown>)
          : item
      );
    } else {
      out[key] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function canSendAnalytics(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof navigator !== 'undefined' && navigator.webdriver) return false;
  if (!isProductionAnalyticsHost(window.location.hostname)) return false;
  return typeof window.gtag === 'function';
}

function hasOnce(key: string): boolean {
  if (memoryOnce.has(key)) return true;
  return localStore()?.getItem(ONCE_STORAGE_PREFIX + key) === '1';
}

function markOnce(key: string) {
  memoryOnce.add(key);
  try {
    localStore()?.setItem(ONCE_STORAGE_PREFIX + key, '1');
  } catch {
    // Storage can throw in private mode. The in-memory set still dedupes this page.
  }
}

export function track<E extends AnalyticsEventName>(
  event: E,
  ...rest: AnalyticsEventMap[E] extends undefined
    ? [params?: undefined, options?: TrackOptions]
    : [params: AnalyticsEventMap[E], options?: TrackOptions]
): void {
  if (!canSendAnalytics()) return;

  const params = rest[0] as Record<string, unknown> | undefined;
  const options = rest[1];
  const clean = scrub(params);

  if (options?.onceKey) {
    if (hasOnce(options.onceKey)) return;
    markOnce(options.onceKey);
  } else if (options?.dedupeKey) {
    if (durableKeys.has(options.dedupeKey)) return;
    durableKeys.add(options.dedupeKey);
  } else {
    const key = `${event}:${JSON.stringify(clean ?? null)}`;
    const now = Date.now();
    const prev = recent.get(key);
    if (prev !== undefined && now - prev < DEDUPE_MS) return;
    recent.set(key, now);
  }

  const payload = options?.beacon
    ? { ...(clean ?? {}), transport_type: 'beacon' }
    : clean;

  try {
    window.gtag!('event', event, payload);
  } catch {
    // A broken tag must not skip checkout, navigation, or account setup.
  }
}

/**
 * GA4 transaction id for a Stripe Checkout Session id (ERI-2647).
 * A cs_ id must not reach Google, so GA gets a short SHA-256 of it: stable for
 * dedupe across tabs and reloads, and not reversible to the session.
 */
export async function purchaseTransactionId(checkoutSessionId: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(checkoutSessionId));
  return (
    'mwp_' +
    Array.from(new Uint8Array(digest).slice(0, 10), (b) => b.toString(16).padStart(2, '0')).join('')
  );
}

/** Never rejects: callers fire it with `void` so it cannot hold up account setup. */
export async function trackPurchase(
  checkoutSessionId: string,
  plan: CheckoutPlan,
  setup?: { already_processed?: boolean; already_processing?: boolean } | null
): Promise<void> {
  if (setup?.already_processed || setup?.already_processing) return;
  try {
    const transactionId = await purchaseTransactionId(checkoutSessionId);
    track(
      'purchase',
      {
        transaction_id: transactionId,
        value: CHECKOUT_VALUE[plan],
        currency: 'USD',
        items: [{ item_name: plan }],
      },
      { onceKey: `purchase:${transactionId}` }
    );
  } catch {
    // No hash (no crypto.subtle) or a broken tag: skip the event, keep setup going.
  }
}

export function trackPlannerRun(locationType: LocationType, dedupeId: string) {
  track(
    'planner_run',
    { location_type: locationType },
    { dedupeKey: `planner_run:${locationType}:${dedupeId}` }
  );
}

export function affiliateDestination(href: string): string | null {
  try {
    const host = new URL(href, 'https://www.milkywayplanner.com').hostname
      .replace(/^www\./, '')
      .toLowerCase();
    const allowed = AFFILIATE_HOSTS.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
    return allowed ? host : null;
  } catch {
    return null;
  }
}

export function trackAffiliateClick(href: string) {
  const destination = affiliateDestination(href);
  if (!destination) return;
  track('affiliate_click', { destination }, { dedupeKey: `affiliate:${href}` });
}

export function checkoutSessionIdFromUrl(url: string): string | null {
  return url.match(CHECKOUT_SESSION_ID)?.[0] ?? null;
}

/**
 * Page location for GA4: origin, path, and query, minus secrets.
 * index.html's gtag config and trackPageView both call this.
 */
export function pageLocationForAnalytics(href: string): string {
  const url = new URL(href);
  const kept = new URLSearchParams();
  url.searchParams.forEach((value, key) => {
    if (!PAGE_LOCATION_QUERY_KEYS_TO_DROP.has(key.toLowerCase())) {
      kept.append(key, value);
    }
  });
  const qs = kept.toString();
  return `${url.origin}${url.pathname}${qs ? `?${qs}` : ''}`;
}

function sweepExpiredPendingDonations(now = Date.now()) {
  const store = localStore();
  if (!store) return;
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key?.startsWith(PENDING_DONATION_PREFIX)) keys.push(key);
  }
  for (const key of keys) {
    try {
      const parsed = JSON.parse(store.getItem(key) || 'null') as { storedAt?: unknown } | null;
      const storedAt = parsed?.storedAt;
      if (typeof storedAt !== 'number' || now - storedAt >= PENDING_DONATION_TTL_MS) {
        store.removeItem(key);
      }
    } catch {
      store.removeItem(key);
    }
  }
}

/**
 * `nonce` is what create-donation-checkout returned. The thanks page sends it to
 * get-session-email, which answers only the browser that holds it (ERI-2782).
 */
export function rememberPendingDonation(value: number, sessionId: string, nonce?: string) {
  try {
    sweepExpiredPendingDonations();
    localStore()?.setItem(
      PENDING_DONATION_PREFIX + sessionId,
      JSON.stringify({ value, currency: 'USD', storedAt: Date.now(), ...(nonce ? { nonce } : {}) })
    );
  } catch {
    // Ignore storage failures. The thanks page fires the donation without an amount.
  }
}

export function readPendingDonation(sessionId: string): { value: number; currency: string } | null {
  try {
    sweepExpiredPendingDonations();
    const raw = localStore()?.getItem(PENDING_DONATION_PREFIX + sessionId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { value?: unknown; currency?: unknown };
    if (typeof parsed.value !== 'number' || typeof parsed.currency !== 'string') return null;
    return { value: parsed.value, currency: parsed.currency };
  } catch {
    return null;
  }
}

/** The email nonce stored with this donation, or null. */
export function readDonationNonce(sessionId: string): string | null {
  try {
    sweepExpiredPendingDonations();
    const raw = localStore()?.getItem(PENDING_DONATION_PREFIX + sessionId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { nonce?: unknown };
    return typeof parsed.nonce === 'string' && DONATION_NONCE.test(parsed.nonce) ? parsed.nonce : null;
  } catch {
    return null;
  }
}

/** Removes the pending donation and its email nonce. */
export function clearPendingDonation(sessionId: string) {
  try {
    localStore()?.removeItem(PENDING_DONATION_PREFIX + sessionId);
  } catch {
    // Ignore.
  }
}

/** Drop session_id from the address bar so a later pageview cannot send it. */
export function stripSessionIdFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search || '');
    if (!params.has('session_id')) return;
    params.delete('session_id');
    const qs = params.toString();
    const hash = window.location.hash || '';
    const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${hash}`;
    window.history.replaceState({}, '', next);
  } catch {
    // URL cleanup must not block setup.
  }
}

export function redirectToCheckout(url: string, plan: CheckoutPlan) {
  try {
    // Beacon so the hit survives the immediate redirect to Stripe.
    track('begin_checkout', { plan }, { beacon: true });
  } finally {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    }
  }
}

export function redirectToDonationCheckout(url: string, value: number, sessionId?: string, nonce?: string) {
  const id = sessionId || checkoutSessionIdFromUrl(url);
  if (id) rememberPendingDonation(value, id, nonce);
  if (typeof window !== 'undefined') {
    window.location.href = url;
  }
}
